/**
 * Feature Request Card Component
 * Displays a feature request with voting controls and status badge
 */

import React from 'react';
import { FeatureRequest, FeatureRequestStatus } from '@creatoros/shared';

interface FeatureRequestCardProps {
  featureRequest: FeatureRequest;
  userVote?: 'up' | 'down' | null;
  onVote?: (voteType: 'up' | 'down') => void;
  onRemoveVote?: () => void;
  onClick?: () => void;
  isVoting?: boolean;
  isOwnRequest?: boolean;
}

/**
 * Get status badge configuration
 */
const getStatusConfig = (status: FeatureRequestStatus) => {
  const config: Record<FeatureRequestStatus, { label: string; color: string; bgColor: string }> = {
    pending: {
      label: 'Pending Review',
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    approved: {
      label: 'Approved',
      color: 'text-green-700',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
    },
    under_review: {
      label: 'Under Review',
      color: 'text-blue-700',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    },
    rejected: {
      label: 'Rejected',
      color: 'text-red-700',
      bgColor: 'bg-red-100 dark:bg-red-900/30',
    },
    in_progress: {
      label: 'In Progress',
      color: 'text-purple-700',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    },
    completed: {
      label: 'Completed',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    },
    declined: {
      label: 'Declined',
      color: 'text-gray-700',
      bgColor: 'bg-gray-100 dark:bg-gray-900/30',
    },
  };
  return config[status] || config.pending;
};

export const FeatureRequestCard: React.FC<FeatureRequestCardProps> = ({
  featureRequest,
  userVote,
  onVote,
  onRemoveVote,
  onClick,
  isVoting = false,
  isOwnRequest = false,
}) => {
  const statusConfig = getStatusConfig(featureRequest.status);

  const handleUpvote = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isVoting || isOwnRequest) return;

    if (userVote === 'up') {
      onRemoveVote?.();
    } else {
      onVote?.('up');
    }
  };

  const handleDownvote = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isVoting || isOwnRequest) return;

    if (userVote === 'down') {
      onRemoveVote?.();
    } else {
      onVote?.('down');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      className={`
        bg-white dark:bg-[#1C2727]
        rounded-lg shadow-sm border border-gray-200 dark:border-gray-700
        p-5
        transition-all duration-200
        hover:shadow-md
        ${onClick ? 'cursor-pointer hover:border-blue-300 dark:hover:border-blue-700' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex gap-4">
        {/* Content Column - now full width */}
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white line-clamp-2">
              {featureRequest.title}
            </h3>
            <span
              className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}
            >
              {statusConfig.label}
            </span>
          </div>

          {/* Description */}
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
            {featureRequest.description}
          </p>

          {/* Meta Row with Vote Counts */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              {featureRequest.submitterName === 'Anonymous User' ? (
                <span className="text-gray-400 dark:text-gray-500 italic">Anonymous</span>
              ) : (
                <span>by {featureRequest.submitterName}</span>
              )}
              <span>•</span>
              <span>{formatDate(featureRequest.createdAt)}</span>
              {featureRequest.attachments.length > 0 && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                      />
                    </svg>
                    {featureRequest.attachments.length}
                  </span>
                </>
              )}
            </div>

            {/* Vote counts - horizontal layout on the right, now clickable */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleUpvote}
                disabled={isVoting || isOwnRequest}
                className={`
                  flex items-center gap-1 transition-all
                  ${userVote === 'up' ? 'opacity-100' : 'opacity-70 hover:opacity-100'}
                  ${isVoting || isOwnRequest ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'}
                `}
                title={isOwnRequest ? "Can't vote on your own request" : 'Upvote this request'}
              >
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
                <span
                  className={`text-sm font-medium ${userVote === 'up' ? 'text-green-600' : 'text-gray-700 dark:text-gray-300'}`}
                >
                  {featureRequest.upvotes}
                </span>
              </button>
              <button
                onClick={handleDownvote}
                disabled={isVoting || isOwnRequest}
                className={`
                  flex items-center gap-1 transition-all
                  ${userVote === 'down' ? 'opacity-100' : 'opacity-70 hover:opacity-100'}
                  ${isVoting || isOwnRequest ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'}
                `}
                title={isOwnRequest ? "Can't vote on your own request" : 'Downvote this request'}
              >
                <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                </svg>
                <span
                  className={`text-sm font-medium ${userVote === 'down' ? 'text-gray-600 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}
                >
                  {featureRequest.downvotes}
                </span>
              </button>
            </div>
          </div>

          {/* Admin Response */}
          {featureRequest.adminResponse && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border-l-4 border-blue-500">
              <p className="text-xs font-medium text-blue-800 dark:text-blue-300 mb-1">
                Official Response
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-200 line-clamp-2">
                {featureRequest.adminResponse}
              </p>
            </div>
          )}

          {/* Rejection Reason */}
          {featureRequest.status === 'rejected' && featureRequest.rejectionReason && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-md border-l-4 border-red-500">
              <p className="text-xs font-medium text-red-800 dark:text-red-300 mb-1">
                Rejection Reason
              </p>
              <p className="text-sm text-red-700 dark:text-red-200 line-clamp-2">
                {featureRequest.rejectionReason}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeatureRequestCard;
